import React from 'react';
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Stack,
  useMediaQuery,
  Theme,
  Slide,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import { TransitionProps } from '@mui/material/transitions';
import { useLocale } from '../../hooks/useLocale';

export interface TranslateConfirmModalProps {
  open: boolean;
  /** Display name of the story's origin language, e.g. "English" */
  originLanguage: string;
  /** Short code for the origin language (shown in chip), e.g. "EN" */
  originCode: string;
  /** Display name of the user's target language, e.g. "Tiếng Việt" */
  targetLanguage: string;
  /** Short code for the target language (shown in chip), e.g. "VI" */
  targetCode: string;
  onConfirm: () => void;
  onKeep: () => void;
  onClose: () => void;
}

const SlideTransition = React.forwardRef(function SlideTransition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const BRAND_GRADIENT = 'linear-gradient(135deg, #2979FF 0%, #00B0FF 100%)';
const GLOW_GRADIENT = 'linear-gradient(135deg, rgba(41,121,255,0.45) 0%, rgba(0,176,255,0.25) 50%, rgba(124,77,255,0.35) 100%)';

const TranslateConfirmModal: React.FC<TranslateConfirmModalProps> = ({
  open,
  originLanguage,
  originCode,
  targetLanguage,
  targetCode,
  onConfirm,
  onKeep,
  onClose,
}) => {
  const theme = useTheme();
  const { t } = useLocale();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery((th: Theme) => th.breakpoints.down('sm'));

  const surface = isDark ? '#1A1D2E' : '#FFFFFF';
  const surfaceElevated = isDark ? '#252836' : '#F4F6FB';
  const textPrimary = isDark ? '#F5F6FA' : '#13151F';
  const textSecondary = isDark ? '#A4AAC6' : '#5A6178';
  const borderColor = isDark ? 'rgba(255,255,255,0.07)' : 'rgba(19,21,31,0.08)';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideTransition}
      maxWidth="xs"
      fullWidth
      fullScreen={false}
      PaperProps={{
        sx: {
          margin: { xs: '16px', sm: '32px' },
          width: { xs: 'calc(100% - 32px)', sm: 460 },
          maxWidth: { xs: 'calc(100% - 32px)', sm: 460 },
          borderRadius: '24px',
          background: surface,
          color: textPrimary,
          border: `1px solid ${borderColor}`,
          overflow: 'hidden',
          position: 'relative',
          boxShadow: isDark
            ? '0 24px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(41,121,255,0.12)'
            : '0 24px 60px rgba(19,21,31,0.18), 0 0 0 1px rgba(41,121,255,0.08)',
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: isDark ? 'rgba(5,6,15,0.72)' : 'rgba(19,21,31,0.55)',
          backdropFilter: 'blur(8px)',
        },
      }}
    >
      {/* Ambient gradient glow behind content */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: -120,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 420,
          height: 260,
          background: GLOW_GRADIENT,
          filter: 'blur(60px)',
          opacity: isDark ? 0.55 : 0.35,
          pointerEvents: 'none',
        }}
      />
      {/* Top brand stripe */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 3,
          background: BRAND_GRADIENT,
        }}
      />

      <IconButton
        onClick={onClose}
        aria-label="close"
        size="small"
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          color: textSecondary,
          zIndex: 2,
          '&:hover': {
            backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(19,21,31,0.05)',
            color: textPrimary,
          },
        }}
      >
        <CloseRoundedIcon fontSize="small" />
      </IconButton>

      <Box sx={{
        position: 'relative',
        px: { xs: 3, sm: 4 },
        pt: { xs: 4, sm: 5 },
        pb: { xs: 3, sm: 4 },
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: 2.5, sm: 3 },
      }}>
        {/* Language Pair Hero */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: { xs: 1.25, sm: 1.75 },
          mt: { xs: 0.5, sm: 1 },
        }}>
          <LanguageChip code={originCode} label={originLanguage} variant="origin" isDark={isDark} />
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 36,
            height: 36,
            borderRadius: '12px',
            background: BRAND_GRADIENT,
            color: '#fff',
            flexShrink: 0,
            boxShadow: '0 6px 18px rgba(41,121,255,0.38)',
          }}>
            <EastRoundedIcon sx={{ fontSize: 20 }} />
          </Box>
          <LanguageChip code={targetCode} label={targetLanguage} variant="target" isDark={isDark} />
        </Box>

        {/* Eyebrow */}
        <Stack direction="row" spacing={0.75} alignItems="center" justifyContent="center">
          <AutoAwesomeRoundedIcon sx={{ fontSize: 14, color: '#00B0FF' }} />
          <Typography
            component="span"
            sx={{
              fontFamily: '"SF Pro Display", "Inter", "Roboto", sans-serif',
              fontSize: { xs: 10.5, sm: 11 },
              fontWeight: 700,
              letterSpacing: '0.16em',
              textTransform: 'uppercase',
              color: '#00B0FF',
            }}
          >
            {t('translate_popup_eyebrow')}
          </Typography>
        </Stack>

        {/* Title */}
        <Typography
          component="h2"
          sx={{
            textAlign: 'center',
            fontFamily: '"SF Pro Display", "Inter", "Roboto", sans-serif',
            fontWeight: 700,
            fontSize: { xs: '1.375rem', sm: '1.625rem' },
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            color: textPrimary,
            mt: -0.5,
          }}
        >
          {t('translate_popup_title', { targetLang: targetLanguage })}
        </Typography>

        {/* Message */}
        <Typography
          sx={{
            textAlign: 'center',
            fontSize: { xs: '0.9375rem', sm: '1rem' },
            lineHeight: 1.6,
            color: textSecondary,
            px: { xs: 0, sm: 1 },
          }}
        >
          {t('translate_popup_message', { originLang: originLanguage, targetLang: targetLanguage })}
        </Typography>

        {/* AI note */}
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1,
          px: 1.5,
          py: 1.25,
          borderRadius: '12px',
          background: surfaceElevated,
          border: `1px solid ${borderColor}`,
        }}>
          <TranslateRoundedIcon sx={{ fontSize: 16, color: '#7C4DFF', flexShrink: 0, mt: 0.125 }} />
          <Typography sx={{
            fontSize: { xs: '0.75rem', sm: '0.8125rem' },
            lineHeight: 1.55,
            color: textSecondary,
          }}>
            {t('translate_popup_note')}
          </Typography>
        </Box>

        {/* Actions */}
        <Stack
          direction={{ xs: 'column-reverse', sm: 'row' }}
          spacing={1.25}
          sx={{ mt: 0.5 }}
        >
          <Button
            onClick={onKeep}
            fullWidth={isMobile}
            disableElevation
            sx={{
              flex: { sm: 1 },
              height: 48,
              borderRadius: '14px',
              color: textPrimary,
              background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(19,21,31,0.05)',
              border: `1px solid ${borderColor}`,
              fontWeight: 600,
              fontSize: '0.9375rem',
              textTransform: 'none',
              '&:hover': {
                background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(19,21,31,0.08)',
              },
            }}
          >
            {t('translate_popup_btn_keep')}
          </Button>
          <Button
            onClick={onConfirm}
            fullWidth={isMobile}
            disableElevation
            sx={{
              flex: { sm: 1.35 },
              height: 48,
              borderRadius: '14px',
              color: '#fff',
              background: BRAND_GRADIENT,
              fontWeight: 700,
              fontSize: '0.9375rem',
              textTransform: 'none',
              boxShadow: '0 8px 24px rgba(41,121,255,0.35), inset 0 1px 0 rgba(255,255,255,0.25)',
              transition: 'transform 160ms ease, box-shadow 160ms ease',
              '&:hover': {
                background: BRAND_GRADIENT,
                transform: 'translateY(-1px)',
                boxShadow: '0 12px 30px rgba(41,121,255,0.45), inset 0 1px 0 rgba(255,255,255,0.3)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
            }}
          >
            {t('translate_popup_btn_translate')}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
};

// ─── Language code chip ─────────────────────────────────────────────
interface LanguageChipProps {
  code: string;
  label: string;
  variant: 'origin' | 'target';
  isDark: boolean;
}

const LanguageChip: React.FC<LanguageChipProps> = ({ code, label, variant, isDark }) => {
  const baseBg = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(19,21,31,0.04)';
  const borderCol = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(19,21,31,0.08)';
  const codeColor = variant === 'target' ? '#00B0FF' : (isDark ? '#C7CCE0' : '#4B5168');

  return (
    <Box sx={{
      flex: '1 1 0',
      minWidth: 0,
      maxWidth: { xs: 110, sm: 140 },
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 0.375,
      px: { xs: 1, sm: 1.5 },
      py: { xs: 1.125, sm: 1.375 },
      borderRadius: '16px',
      background: baseBg,
      border: `1px solid ${borderCol}`,
      position: 'relative',
      ...(variant === 'target' && {
        background: isDark
          ? 'linear-gradient(135deg, rgba(41,121,255,0.14) 0%, rgba(0,176,255,0.08) 100%)'
          : 'linear-gradient(135deg, rgba(41,121,255,0.08) 0%, rgba(0,176,255,0.04) 100%)',
        borderColor: 'rgba(0,176,255,0.35)',
      }),
    }}>
      <Typography
        component="span"
        sx={{
          fontFamily: '"SF Mono", "Menlo", monospace',
          fontWeight: 700,
          fontSize: { xs: '1rem', sm: '1.125rem' },
          letterSpacing: '0.05em',
          color: codeColor,
          lineHeight: 1,
        }}
      >
        {code.toUpperCase()}
      </Typography>
      <Typography
        component="span"
        sx={{
          fontSize: { xs: '0.6875rem', sm: '0.75rem' },
          color: isDark ? '#8A8FA8' : '#6B7083',
          fontWeight: 500,
          lineHeight: 1.2,
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          width: '100%',
        }}
      >
        {label}
      </Typography>
    </Box>
  );
};

export default TranslateConfirmModal;
